import React from 'react';

import type { PopoverProps } from '@mui/material';
import { InputAdornment, InputBase, Paper, Popover } from '@mui/material';
import { MagnifyingGlassIcon } from 'src/components/icons/MagnifyingGlassIcon';
import { important } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import type { ContractTypeAutocompleteProps } from './ContractTypeAutocomplete';
import { ContractTypeAutocomplete } from './ContractTypeAutocomplete';

const useStyles = makeStyles()(theme => ({
  disablePortal: {
    position: 'static',
  },
  input: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    padding: theme.spacing(1),
  },
  listBox: {
    maxHeight: 240,
    overflow: 'auto',
  },
  listPaper: {
    boxShadow: 'unset',
    margin: 'unset',
    maxHeight: 'none',
  },
  popper: {
    '& .MuiAutocomplete-popper': {
      position: important('static'),
      transform: important('none'),
    },
    borderRadius: 3,
    overflow: 'visible',
    width: 240,
  },
}));

interface ContractTypePopperProps extends Pick<
  ContractTypeAutocompleteProps,
  'onChange' | 'renderOption' | 'suggestNewOption' | 'value'
> {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  open: boolean;
  disabledReason?: string;
  popoverProps?: Partial<PopoverProps>;
}

export function ContractTypePopper({
  anchorEl,
  disabledReason,
  onChange,
  onClose,
  open,
  popoverProps = {
    anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
    transformOrigin: { horizontal: 'left', vertical: 'top' },
  } satisfies Partial<PopoverProps>,
  renderOption,
  suggestNewOption,
  value,
}: ContractTypePopperProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <Popover anchorEl={anchorEl} open={open} onClose={onClose} {...popoverProps}>
      <Paper className={classes.popper} onClick={e => e.stopPropagation()}>
        <ContractTypeAutocomplete
          autoHighlight
          disableCloseOnSelect
          disablePortal
          multiple
          open
          classes={{
            listbox: classes.listBox,
            paper: classes.listPaper,
            popperDisablePortal: classes.disablePortal,
          }}
          disabled={Boolean(disabledReason)}
          refetch={open}
          renderInput={props => {
            const { slotProps, ...rest } = props;

            return (
              <InputBase
                ref={slotProps.input.ref}
                autoFocus
                className={classes.input}
                inputProps={slotProps.htmlInput}
                startAdornment={
                  <InputAdornment position="start">
                    <MagnifyingGlassIcon />
                  </InputAdornment>
                }
                {...rest}
              />
            );
          }}
          renderOption={renderOption}
          suggestNewOption={suggestNewOption}
          value={value}
          onChange={onChange}
        />
      </Paper>
    </Popover>
  );
}
